import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _sortBy from 'lodash/sortBy';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import ErrorMessage from '@minecraft.error-message';
import { helpers } from '@minecraft.utils';
import { gqlProjectStatusEnum, gqlProjectStatus } from '@minecraft.graphql-types';
import { QUERY_PROJECT_STATUS } from '../FilterBasicCollectionGraphQL';
import { useTranslation } from '../../../../I18N';
import { generateSelectOptionsBasedOnField, getFilteredArrayByEnabled } from '../../../shared/utils';
import { FilterData } from '../../../shared/types';

type Props = FilterData & { viewExclusiveStatus: boolean };

const defaultOptions = [
  gqlProjectStatusEnum.ACTIVE,
  gqlProjectStatusEnum.ARCHIVED,
  gqlProjectStatusEnum.SUBMISSIONS_CLOSED,
  gqlProjectStatusEnum.DRAFT,
  gqlProjectStatusEnum.MODIFICATIONS_COMPLETE,
  gqlProjectStatusEnum.MODIFICATIONS_REQUESTED,
  gqlProjectStatusEnum.REJECTED,
  gqlProjectStatusEnum.EDIT_TO_ACTIVE_PROJECT,
];
const castingWorkflowOptions = [
  gqlProjectStatusEnum.ACTIVE,
  gqlProjectStatusEnum.ARCHIVED,
  gqlProjectStatusEnum.SUBMISSIONS_CLOSED,
  gqlProjectStatusEnum.NOT_YET_PUBLISHED,
];

const ProjectStatus: FC<Props> = ({ name, label, viewExclusiveStatus }) => {
  const { t } = useTranslation();
  const [getProjectStatus, { data, loading, error }] = useLazyQuery(QUERY_PROJECT_STATUS);

  const handleProjectStatusFetch = useCallback((): void => {
    if (!data) {
      getProjectStatus();
    }
  }, [data, getProjectStatus]);

  const projectStatusOptions = useMemo(() => {
    let statusOptions: gqlProjectStatus[] = [];

    if (!viewExclusiveStatus) {
      statusOptions = data?.projectStatus.filter((f: gqlProjectStatus) =>
        castingWorkflowOptions.includes(f.code as gqlProjectStatusEnum)
      );
    } else {
      statusOptions = data?.projectStatus.filter((f: gqlProjectStatus) =>
        defaultOptions.includes(f.code as gqlProjectStatusEnum)
      );
    }

    // need to change the label displayed for select values
    const updatedLabelNames = statusOptions?.map((s) => {
      const { displayName } = helpers.getProjectStatusInfo(viewExclusiveStatus, s, t);

      return {
        ...s,
        name: displayName,
      };
    });

    return generateSelectOptionsBasedOnField(
      _sortBy(getFilteredArrayByEnabled(updatedLabelNames), 'sortOrder'),
      'name',
      false,
      undefined,
      true
    );
  }, [data, t, viewExclusiveStatus]);

  if (error) return <ErrorMessage error={error} />;

  return (
    <DropdownWithChildren
      withFormik
      withSelectAll
      isLoading={loading}
      name={name}
      toggleLabel={label}
      options={projectStatusOptions}
      onOptionsFetch={handleProjectStatusFetch}
    />
  );
};

export default memo(ProjectStatus);
